// src/components/MapboxFieldView.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { addTerrainLayer, SATELLITE_STYLE } from "../utils/mapTerrain.js";

/**
 * MapboxFieldView
 * - показує полігони полів (features або geometry)
 * - підсвічує selectedId
 * - flyToId -> fitBounds до конкретного поля
 * - ✅ "Я на карті" (Geolocate)
 * - ✅ "Пін" (ставити мітку)
 * - ✅ "Маршрут" (відкрити Google Maps до вибраного поля / pin / bbox)
 */
export default function MapboxFieldView({
  height = 360,
  geometry,
  bbox,
  features, // масив полів
  selectedId,
  flyToId,
  onSelect,
  styleUrl = SATELLITE_STYLE,

  // ✅ нове
  enableGeolocate = true, // показати кнопку "Я"
  enableDropPin = true, // кнопка "Пін"
  enableNavigate = true, // кнопка "Маршрут"
  defaultCenter = [30.5234, 50.4501],
  defaultZoom = 9,
}) {
  const mapRef = useRef(null);
  const elRef = useRef(null);

  const geolocateRef = useRef(null);
  const myMarkerRef = useRef(null);
  const myLngLatRef = useRef(null);

  const pinMarkerRef = useRef(null);
  const pinLngLatRef = useRef(null); // ✅ зберігаємо точку піну тут (щоб стабільно використовувати)
  const [pinMode, setPinMode] = useState(false);
  const [hasPin, setHasPin] = useState(false);

  const [err, setErr] = useState(null);

  const token = process.env.REACT_APP_MAPBOX_TOKEN;

  const parseGeom = useCallback((g) => {
    try {
      if (!g) return null;
      return typeof g === "string" ? JSON.parse(g) : g;
    } catch {
      return null;
    }
  }, []);

  const bboxFromGeometry = useCallback((geom) => {
    try {
      if (!geom) return null;

      const acc = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
      const pushPoint = (p) => {
        const x = Number(p?.[0]);
        const y = Number(p?.[1]);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        acc.minX = Math.min(acc.minX, x);
        acc.minY = Math.min(acc.minY, y);
        acc.maxX = Math.max(acc.maxX, x);
        acc.maxY = Math.max(acc.maxY, y);
      };

      if (geom.type === "Polygon") {
        for (const ring of geom.coordinates || []) for (const p of ring || []) pushPoint(p);
      } else if (geom.type === "MultiPolygon") {
        for (const poly of geom.coordinates || [])
          for (const ring of poly || [])
            for (const p of ring || []) pushPoint(p);
      } else {
        return null;
      }

      if (!Number.isFinite(acc.minX) || !Number.isFinite(acc.maxX)) return null;
      if (acc.maxX - acc.minX === 0 || acc.maxY - acc.minY === 0) return null;

      return [acc.minX, acc.minY, acc.maxX, acc.maxY];
    } catch {
      return null;
    }
  }, []);

  const fitToBbox = useCallback((map, bb, padding = 60) => {
    try {
      if (!bb || bb.length !== 4) return;
      map.fitBounds(
        [
          [bb[0], bb[1]],
          [bb[2], bb[3]],
        ],
        { padding, duration: 700 }
      );
    } catch {}
  }, []);

  const getFieldBbox = useCallback(
    (f) => {
      if (!f) return null;

      // Спочатку — з актуальної геометрії, щоб стале bbox з бека не збивало центр
      const geom = parseGeom(f.geometry ?? f.geometryJson ?? f.geometry_json);
      const fromGeom = bboxFromGeometry(geom);
      if (fromGeom) return fromGeom;

      // Fallback — збережені bbox (якщо геометрії немає взагалі)
      const hasBBoxNums = [f.bboxMinLng, f.bboxMinLat, f.bboxMaxLng, f.bboxMaxLat].every((v) =>
        Number.isFinite(Number(v))
      );
      if (hasBBoxNums) {
        return [Number(f.bboxMinLng), Number(f.bboxMinLat), Number(f.bboxMaxLng), Number(f.bboxMaxLat)];
      }

      return null;
    },
    [bboxFromGeometry, parseGeom]
  );

  const getFieldCenter = useCallback(
    (f) => {
      const bb = getFieldBbox(f);
      if (!bb) return null;
      const cx = (bb[0] + bb[2]) / 2;
      const cy = (bb[1] + bb[3]) / 2;
      if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
      return { lng: cx, lat: cy };
    },
    [getFieldBbox]
  );

  // ---- init map ----
  useEffect(() => {
    if (!token) {
      setErr("Нема REACT_APP_MAPBOX_TOKEN у .env");
      return;
    }
    setErr(null);

    if (mapRef.current) return;

    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: elRef.current,
      style: styleUrl,
      center: defaultCenter,
      zoom: defaultZoom,
    });

    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    // ✅ Geolocate (показати себе)
    if (enableGeolocate) {
      const geolocate = new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true,
        showAccuracyCircle: true,
      });
      geolocateRef.current = geolocate;
      map.addControl(geolocate, "top-right");

      geolocate.on("geolocate", (pos) => {
        try {
          const lng = Number(pos?.coords?.longitude);
          const lat = Number(pos?.coords?.latitude);
          if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

          myLngLatRef.current = { lng, lat };

          if (!myMarkerRef.current) {
            const dot = document.createElement("div");
            dot.style.width = "12px";
            dot.style.height = "12px";
            dot.style.borderRadius = "999px";
            dot.style.background = "rgba(59,130,246,0.95)";
            dot.style.boxShadow = "0 0 0 4px rgba(59,130,246,0.25)";
            myMarkerRef.current = new mapboxgl.Marker({ element: dot }).setLngLat([lng, lat]).addTo(map);
          } else {
            myMarkerRef.current.setLngLat([lng, lat]);
          }
        } catch {}
      });
    }

    mapRef.current = map;

    return () => {
      try {
        map.remove();
      } catch {}
      mapRef.current = null;
      geolocateRef.current = null;

      try {
        myMarkerRef.current?.remove();
      } catch {}
      myMarkerRef.current = null;
      myLngLatRef.current = null;

      try {
        pinMarkerRef.current?.remove();
      } catch {}
      pinMarkerRef.current = null;
      pinLngLatRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, styleUrl, enableGeolocate]);

  // ---- featureCollection ----
  const featureCollection = useMemo(() => {
    if (Array.isArray(features) && features.length > 0) {
      const feats = [];

      for (const f of features) {
        const geom = parseGeom(f.geometry);
        if (!geom || (geom.type !== "Polygon" && geom.type !== "MultiPolygon")) continue;

        feats.push({
          type: "Feature",
          properties: {
            id: String(f.id),
            name: String(f.name || ""),
            areaHa: Number(f.areaHa || 0),
          },
          geometry: geom,
        });
      }

      return { type: "FeatureCollection", features: feats };
    }

    const g = parseGeom(geometry);
    if (!g || (g.type !== "Polygon" && g.type !== "MultiPolygon")) return null;

    return {
      type: "FeatureCollection",
      features: [{ type: "Feature", properties: { id: "single" }, geometry: g }],
    };
  }, [features, geometry, parseGeom]);

  // ---- apply source + layers + click ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !featureCollection) return;

    const srcId = "fields-geojson";
    const fillId = "fields-fill";
    const lineId = "fields-line";

    const apply = () => {
      addTerrainLayer(map);
      if (map.getSource(srcId)) {
        map.getSource(srcId).setData(featureCollection);
      } else {
        map.addSource(srcId, { type: "geojson", data: featureCollection });
      }

      if (!map.getLayer(fillId)) {
        map.addLayer({
          id: fillId,
          type: "fill",
          source: srcId,
          paint: {
            "fill-color": "rgba(34,197,94,0.30)",
            "fill-opacity": ["case", ["==", ["get", "id"], String(selectedId || "")], 0.46, 0.20],
            "fill-outline-color": "rgba(34,197,94,0.95)",
          },
        });
      }

      if (!map.getLayer(lineId)) {
        map.addLayer({
          id: lineId,
          type: "line",
          source: srcId,
          paint: {
            "line-color": "rgba(34,197,94,0.95)",
            "line-width": ["case", ["==", ["get", "id"], String(selectedId || "")], 4, 2.4],
          },
        });
      }

      const onEnter = () => (map.getCanvas().style.cursor = "pointer");
      const onLeave = () => (map.getCanvas().style.cursor = "");

      map.on("mouseenter", fillId, onEnter);
      map.on("mouseleave", fillId, onLeave);

      const onClick = (e) => {
        try {
          const feat = e?.features?.[0];
          const id = feat?.properties?.id;
          if (id && onSelect) onSelect(String(id));
        } catch {}
      };

      map.on("click", fillId, onClick);

      return () => {
        try {
          map.off("mouseenter", fillId, onEnter);
          map.off("mouseleave", fillId, onLeave);
          map.off("click", fillId, onClick);
        } catch {}
      };
    };

    if (map.isStyleLoaded()) {
      const cleanup = apply();
      return cleanup;
    } else {
      const onLoad = () => {
        const cleanup = apply();
        map.__fields_cleanup__ = cleanup;
      };
      map.once("load", onLoad);
      return () => {
        try {
          map.off("load", onLoad);
        } catch {}
        try {
          map.__fields_cleanup__?.();
        } catch {}
        map.__fields_cleanup__ = null;
      };
    }
  }, [featureCollection, onSelect, selectedId]);

  // ---- update paints when selectedId changes ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const fillId = "fields-fill";
    const lineId = "fields-line";

    try {
      if (map.getLayer(fillId)) {
        map.setPaintProperty(fillId, "fill-opacity", [
          "case",
          ["==", ["get", "id"], String(selectedId || "")],
          0.46,
          0.20,
        ]);
      }
      if (map.getLayer(lineId)) {
        map.setPaintProperty(lineId, "line-width", [
          "case",
          ["==", ["get", "id"], String(selectedId || "")],
          4,
          2.4,
        ]);
      }
    } catch {}
  }, [selectedId]);

  // ---- SINGLE MODE fit ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!features && bbox && bbox.length === 4) {
      fitToBbox(map, bbox, 40);
    }
  }, [bbox, features, fitToBbox]);

  // ✅ flyToId -> ALWAYS fit to that field
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!Array.isArray(features) || features.length === 0) return;
    if (!flyToId) return;

    const f = features.find((x) => String(x.id) === String(flyToId));
    if (!f) return;

    const bb = getFieldBbox(f);
    if (bb) fitToBbox(map, bb, 70);
  }, [flyToId, features, fitToBbox, getFieldBbox]);

  // ---- initial fit all fields once ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (Array.isArray(features) && features.length > 0 && !selectedId) {
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      for (const f of features) {
        const bb = getFieldBbox(f);
        if (!bb) continue;

        minX = Math.min(minX, bb[0]);
        minY = Math.min(minY, bb[1]);
        maxX = Math.max(maxX, bb[2]);
        maxY = Math.max(maxY, bb[3]);
      }

      if (Number.isFinite(minX) && Number.isFinite(maxX)) {
        fitToBbox(map, [minX, minY, maxX, maxY], 60);
      }
    }
  }, [features, selectedId, fitToBbox, getFieldBbox]);

  // ✅ Pin mode: клік по карті ставить маркер
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !enableDropPin) return;

    const onMapClick = (e) => {
      try {
        if (!pinMode) return;

        const lng = Number(e?.lngLat?.lng);
        const lat = Number(e?.lngLat?.lat);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

        // ✅ зберігаємо точку
        pinLngLatRef.current = { lng, lat };
        setHasPin(true);

        // ✅ красивий кастомний маркер
        if (!pinMarkerRef.current) {
          const wrap = document.createElement("div");
          wrap.style.width = "18px";
          wrap.style.height = "18px";
          wrap.style.borderRadius = "999px";
          wrap.style.background = "rgba(245,158,11,0.95)";
          wrap.style.boxShadow = "0 0 0 4px rgba(245,158,11,0.25)";
          wrap.style.border = "1px solid rgba(255,255,255,0.55)";
          pinMarkerRef.current = new mapboxgl.Marker({ element: wrap }).setLngLat([lng, lat]).addTo(map);
        } else {
          pinMarkerRef.current.setLngLat([lng, lat]);
        }

        setPinMode(false);
      } catch {}
    };

    map.on("click", onMapClick);
    return () => {
      try {
        map.off("click", onMapClick);
      } catch {}
    };
  }, [pinMode, enableDropPin]);

  const handleLocateMe = () => {
    try {
      geolocateRef.current?.trigger();
    } catch {}
  };

  const handleTogglePin = () => {
    if (!enableDropPin) return;
    setPinMode((v) => !v);
  };

  const handleClearPin = () => {
    try {
      pinMarkerRef.current?.remove();
    } catch {}
    pinMarkerRef.current = null;
    pinLngLatRef.current = null;
    setHasPin(false);
    setPinMode(false);
  };

  const handleNavigate = () => {
    if (!enableNavigate) return;

    // ✅ НОВИЙ пріоритет:
    // 1) pin (якщо є)
    // 2) selectedId / flyToId (поле)
    // 3) bbox single mode
    let target = null;

    // 1) pin
    const pll = pinLngLatRef.current;
    if (pll && Number.isFinite(pll.lng) && Number.isFinite(pll.lat)) {
      target = { lng: pll.lng, lat: pll.lat };
    }

    // 2) field center
    if (!target && Array.isArray(features) && features.length > 0) {
      const id = selectedId || flyToId;
      if (id) {
        const f = features.find((x) => String(x.id) === String(id));
        if (f) target = getFieldCenter(f);
      }
    }

    // 3) bbox center
    if (!target && bbox && bbox.length === 4) {
      const cx = (Number(bbox[0]) + Number(bbox[2])) / 2;
      const cy = (Number(bbox[1]) + Number(bbox[3])) / 2;
      if (Number.isFinite(cx) && Number.isFinite(cy)) target = { lng: cx, lat: cy };
    }

    if (!target) return;

    const dest = `${target.lat},${target.lng}`;

    const originObj = myLngLatRef.current;
    const origin =
      originObj && Number.isFinite(originObj.lng) && Number.isFinite(originObj.lat)
        ? `${originObj.lat},${originObj.lng}`
        : null;

    const url = origin
      ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(
          dest
        )}&travelmode=driving`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}&travelmode=driving`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div style={{ position: "relative" }}>
      {err ? (
        <div
          style={{
            padding: 10,
            borderRadius: 12,
            border: "1px solid rgba(251,113,133,0.45)",
            background: "rgba(0,0,0,0.5)",
            color: "rgba(255,255,255,0.9)",
            marginBottom: 10,
          }}
        >
          {err}
        </div>
      ) : null}

      {/* ✅ Buttons overlay */}
      <div
        style={{
          position: "absolute",
          zIndex: 5,
          left: 12,
          top: 12,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          pointerEvents: "none",
        }}
      >
        {enableGeolocate ? (
          <button type="button" onClick={handleLocateMe} style={btnStyle({ kind: "ghost" })}>
            <span style={btnIconStyle()}>📍</span>
            <span>Я</span>
          </button>
        ) : null}

        {enableDropPin ? (
          <>
            <button
              type="button"
              onClick={handleTogglePin}
              style={btnStyle({ kind: pinMode ? "active" : hasPin ? "ok" : "ghost" })}
              title={pinMode ? "Клікни на мапі щоб поставити пін" : "Поставити пін"}
            >
              <span style={btnIconStyle()}>{pinMode ? "🎯" : "📌"}</span>
              <span>{pinMode ? "Обрати точку" : hasPin ? "Пін ✓" : "Пін"}</span>
            </button>

            {hasPin ? (
              <button type="button" onClick={handleClearPin} style={btnStyle({ kind: "danger" })} title="Скинути пін">
                <span style={btnIconStyle()}>✕</span>
                <span>Скинути</span>
              </button>
            ) : null}
          </>
        ) : null}

        {enableNavigate ? (
          <button type="button" onClick={handleNavigate} style={btnStyle({ kind: "primary" })}>
            <span style={btnIconStyle()}>🧭</span>
            <span>Маршрут</span>
          </button>
        ) : null}
      </div>

      <div
        ref={elRef}
        style={{
          height,
          width: "100%",
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      />
    </div>
  );
}

/** ---------- styles ---------- */

function btnIconStyle() {
  return {
    display: "inline-flex",
    width: 18,
    height: 18,
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "18px",
    fontSize: 14,
    transform: "translateY(-0.5px)",
  };
}

function btnStyle({ kind = "ghost" } = {}) {
  const base = {
    pointerEvents: "auto",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "rgba(255,255,255,0.96)",
    padding: "9px 12px",
    borderRadius: 14,
    fontSize: 13,
    fontWeight: 800,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    cursor: "pointer",
    userSelect: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    whiteSpace: "nowrap",
    boxShadow: "0 10px 30px rgba(0,0,0,0.22)",
  };

  if (kind === "primary") {
    return {
      ...base,
      background: "rgba(59,130,246,0.26)",
      border: "1px solid rgba(59,130,246,0.45)",
    };
  }

  if (kind === "active") {
    return {
      ...base,
      background: "rgba(34,197,94,0.25)",
      border: "1px solid rgba(34,197,94,0.55)",
    };
  }

  if (kind === "ok") {
    return {
      ...base,
      background: "rgba(245,158,11,0.22)",
      border: "1px solid rgba(245,158,11,0.55)",
    };
  }

  if (kind === "danger") {
    return {
      ...base,
      background: "rgba(239,68,68,0.18)",
      border: "1px solid rgba(239,68,68,0.42)",
    };
  }

  // ghost
  return {
    ...base,
    background: "rgba(0,0,0,0.45)",
  };
}